
export enum PlateType {
  FRONT = 'FRONT',
  REAR = 'REAR',
}

export enum FlagType {
  NONE = 'NONE',
  GB = 'GB',
  UK = 'UK',
  ENG = 'ENG',
  SCO = 'SCO',
  CYM = 'CYM',
  EV = 'EV',
  EU = 'EU',
  CUSTOM = 'CUSTOM'
}

export enum PlateSize {
  REGULAR = 'REGULAR', // 520x111
  SHORT_7 = 'SHORT_7', // 460x111
  SHORT_6 = 'SHORT_6', // 399x111
  SHORT_5 = 'SHORT_5', // 338x111
  OVERSIZE = 'OVERSIZE', // 533x152
  MOTO_MINI = 'MOTO_MINI', // 177x127
  MOTO_COMPACT = 'MOTO_COMPACT', // 203x152
  MOTO_MID = 'MOTO_MID', // 226x158
  MOTO_MICRO = 'MOTO_MICRO', // 155x110
  MOTO_LARGE = 'MOTO_LARGE', // 228x177
  JDM = 'JDM', // 330x165
  SQUARE_4X4 = 'SQUARE_4X4', // 280x203
  HEX_5 = 'HEX_5', // 388x111
  HEX_6 = 'HEX_6', // 444x111
  HEX_7 = 'HEX_7', // 519x111
}

export interface Dimensions {
  width: number;
  height: number;
  label?: string;
  viewBox?: string;
  pathData?: string;
  pathTransform?: string; // e.g. "matrix(-1,0,0,1,0,0)"
  isHex?: boolean; 
  cornerSize?: number; 
}

// Hex Path Data (Standardized for 3.5:1 ratio roughly)
const HEX_5_PATH = "m0,98.52c-.03,1.06.15,2.12.52,3.12l80.15,213h938.49s0,0,0,0l80.15-213c.76-2.01.69-4.24-.19-6.2L1056.4,0H43.46L.71,95.43c-.44.97-.67,2.02-.71,3.08Z";
const HEX_5_VIEWBOX = "0 0 1099.85 314.65";

export const PlateDimensions: Record<string, Dimensions> = {
  [PlateSize.REGULAR]: { width: 520, height: 111, label: 'Regular (520x111mm)' },
  [PlateSize.SHORT_7]: { width: 460, height: 111, label: 'Short 7 (460x111mm)' },
  [PlateSize.SHORT_6]: { width: 399, height: 111, label: 'Short 6 (399x111mm)' },
  [PlateSize.SHORT_5]: { width: 338, height: 111, label: 'Short 5 (338x111mm)' },
  
  // Rectangular Sizes
  [PlateSize.OVERSIZE]: { width: 533, height: 152, label: 'Oversize (21x6 - 533x152)' },
  [PlateSize.MOTO_MINI]: { width: 177, height: 127, label: 'Moto Mini (5x7 - 177x127)' },
  [PlateSize.MOTO_COMPACT]: { width: 203, height: 152, label: 'Moto Compact (6x8 - 203x152)' },
  [PlateSize.MOTO_MID]: { width: 226, height: 158, label: 'Moto Mid (6.5x8.5 - 226x158)' },
  [PlateSize.MOTO_MICRO]: { width: 155, height: 110, label: 'Moto Micro (4x6 - 155x110)' },
  [PlateSize.MOTO_LARGE]: { width: 228, height: 177, label: 'Moto Large (7x9 - 228x177)' },
  [PlateSize.JDM]: { width: 330, height: 165, label: 'JDM (13x6.5 - 330x165)' },
  [PlateSize.SQUARE_4X4]: { width: 280, height: 203, label: '4x4 Square (8x11 - 280x203)' },

  // Hex Plates
  [PlateSize.HEX_5]: { 
    width: 388, 
    height: 111, 
    label: 'Hex 5 (388x111mm)',
    viewBox: HEX_5_VIEWBOX,
    pathData: HEX_5_PATH,
    isHex: true
  },
  [PlateSize.HEX_6]: { 
    width: 444, 
    height: 111, 
    label: 'Hex 6 (444x111mm)',
    viewBox: "-1379.165894 -765.321899 444.000000 111.000000",
    pathData: "M950.53064,-765.321899L1363.800049,-765.321899L1378.915161,-731.655029C1379.226074,-730.962158,1379.250366,-730.17627,1378.982422,-729.465942L1350.637207,-654.321899L963.69342,-654.321899L935.349304,-729.464966C935.081421,-730.175171,935.105591,-730.961182,935.416626,-731.653931L950.53064,-765.321899z",
    pathTransform: "matrix(-1,0,0,1,0,0)",
    isHex: true
  },
  [PlateSize.HEX_7]: { 
    width: 519, 
    height: 111, 
    label: 'Hex 7 (519x111mm)',
    viewBox: "-1398.500000 -621.500305 519.000000 111.000000",
    pathData: "M908.023865,-510.500305L879.683411,-585.643555C879.415527,-586.35376,879.439697,-587.139709,879.750671,-587.83252L894.86322,-621.499084L1383.136719,-621.500305L1398.249268,-587.833801C1398.560181,-587.140991,1398.584473,-586.355042,1398.316406,-585.644775L1369.976074,-510.501587L908.023865,-510.500305z",
    pathTransform: "matrix(-1,0,0,1,0,0)",
    isHex: true
  }
};

export interface TextConfig {
  text: string;
  fontFamily: string;
  fontSize: number; // in mm
  color: string;
  show: boolean;
  bold?: boolean;
  italic?: boolean;
  outline?: boolean; // Separate outline/stroke effect
  xOffset?: number; // Extra offset for BSAU
  yOffset?: number; // Extra offset for BSAU
}

export enum TextEffect {
  NONE = 'NONE',
  SHADOW_3D = 'SHADOW_3D',
  CARBON = 'CARBON',
  HONEYCOMB = 'HONEYCOMB',
  HIGHLINE = 'HIGHLINE'
}

export interface BadgeConfig {
  enabled: boolean;
  type: FlagType;
  customSvgUrl: string;
  backgroundColor: 'BLUE' | 'GREEN' | 'BLACK' | 'NONE';
  isVertical: boolean;
  showIdentifier: boolean;
  identifierText: string;
  borderRadius?: number;
  graphicScale?: number;
  graphicVOffset?: number;
  identifierTextSize?: number;
  identifierTextColor?: string;
  identifierTextVOffset?: number;
}

export interface PlateConfig {
  registration: string;
  type: PlateType;
  
  badge: BadgeConfig;
  flagLayout: boolean;
  showFlagGraphic: boolean;

  borderCornerRadius?: number;
  cornerRadius?: number;

  plateSize: string; 
  
  isShowPlate: boolean;
  showPlateOverrides: {
    removeBSAU: boolean; 
    removeDealer: boolean; 
    reduceSpace: boolean; 
  };
  
  mainFontFamily: string;
  mainFontSize: number;
  mainTextLineHeight: number;
  mainTextVerticalOffset: number;
  mainTextHeightScale: number;
  mainTextAlignment: 'center' | 'left' | 'right';
  mainTextBold?: boolean;
  mainTextItalic?: boolean;
  enableKerning: boolean;
  letterSpacing: number; 
  textColor: string;
  forceUppercase: boolean;
  textEffect: TextEffect;
  
  globalOffsetX: number;
  globalOffsetY: number;

  border: boolean;
  borderThickness: number;
  borderMargin: number;
  borderColor: string;

  isEV: boolean;

  dealerText: TextConfig;
  useDealerLogo: boolean;
  dealerLogo?: string;
  dealerLogoHeight: number; 
  
  dealerPostcode: string;
  dealerPostcodeConfig: {
    fontSize: number;
    color: string;
    fontFamily: string;
    fontFamilyOverride?: string;
    bold?: boolean;
    italic?: boolean;
  };
  dealerSeparator: number;
  
  dealerLocation: 'ON_BORDER' | 'ABOVE_BORDER';
  dealerVerticalOffset: number;
  
  slogan: string; 
  sloganConfig: TextConfig;
  sloganLocation: 'ON_BORDER' | 'ABOVE_BORDER' | 'CUSTOM';

  supplierInCorner: boolean; 
  
  bsauText: TextConfig; 
  bsauLocation: 'ON_BORDER' | 'ABOVE_BORDER' | 'BELOW_BORDER' | 'CUSTOM'; 

  // Lipped Plate Config
  lippedPlate: boolean;
  lipImage: string; // Base64 (legacy single image)
  lipHeight: number; // in mm
  lipWidth?: number; // in mm (defaults to plate width if not set)
  lipBackgroundColor?: string; // Background color when no image (defaults to plate color)
  lipText: LipTextConfig; // Legacy single text (for backward compatibility)
  lipTexts?: LipTextItem[]; // Multiple text boxes
  lipImages: LipImageItem[];
}

export interface LipTextConfig {
  enabled: boolean;
  text: string;
  fontFamily: string;
  fontSize: number;
  color: string;
  bold: boolean;
  italic: boolean;
  xOffset: number;
  yOffset: number;
}

export interface LipTextItem {
  id: string;
  enabled: boolean;
  text: string;
  fontFamily: string;
  fontSize: number;
  color: string;
  bold: boolean;
  italic: boolean;
  xOffset: number;
  yOffset: number;
}

export interface LipImageItem {
  id: string;
  image: string; // Base64
  width: number;  // mm
  height: number; // mm
  xOffset: number;
  yOffset: number;
}

export interface GenerationResult {
  suggestion: string;
  reasoning: string;
}

export interface LicenseInfo {
  key: string;
  hwId: string;
  tier: 'BASIC' | 'PROFESSIONAL';
  expiryDate: number;
  contactName: string;
  companyName: string;
  isTrial?: boolean;
}

export interface User {
  email?: string;
  name?: string;
  license?: LicenseInfo;
  status: 'PENDING' | 'APPROVED' | 'ACTIVE';
}

export interface PrinterProfile {
  id: string;
  name: string; 
  systemPrinterName: string;
  // Android Specific
  androidAddress?: string; // BT MAC or IP
  androidPort?: string;
  connectionType?: 'usb' | 'bluetooth' | 'wifi' | 'usb_oki';
  printerLanguage?: 'TSPL' | 'CPCL' | 'ESC';
  driverType?: 'android_tspl' | 'oki_usb' | 'godex' | 'tsc' | 'citizen' | 'meena_lite';
  godexSpeed?: number;
  godexDarkness?: number;
  godexPdfMode?: 'sdk' | 'gepl' | 'legacy_bitmap' | 'ezpl_hex';
  offsetX: number; 
  offsetY: number;
  lengthCalibration: number; // Percentage
  lengthCalibrationEnabled?: boolean; // Enable length calibration stretch
  rotate: boolean; 
  mirror: boolean; 
  enableCropMarks?: boolean;
  cropMarksOffset?: number; // Bleed amount in mm - adds whitespace in Flexible mode, controls mark position in Fixed mode
  // Guidelines settings (for aligning acrylic blocks on short plates)
  enableSideGuidelines?: boolean;
  sideGuidelinesPosition?: 'side' | 'top' | 'both';
  sideGuidelinesOffset?: number; // Offset outwards from artwork edge (0 = at edge, positive = into whitespace)
  // Media size mode
  mediaSize?: 'FLEXIBLE' | 'FIXED'; // FLEXIBLE = artwork + bleed, FIXED = exact pageWidth x pageHeight
  dpi?: number;
  // Printer Command Settings (Android)
  pageWidth?: number; // mm - default 100
  pageHeight?: number; // mm - default 150
  paperType?: 'label' | 'continuous' | 'black_label'; // default 'label'
  gapEnabled?: boolean; // For TSPL gap detection - default false
  compress?: boolean; // Compress image data - default false
  cutAfterPrint?: boolean; // Auto-cut after print - default false
  feedEnabled?: boolean; // Feed paper after print - default false
  feedAmount?: number; // Feed amount in mm (1-20) - default 5
  // OKI Printer PJL Settings
  okiMediaSource?: 'AUTO' | 'TRAY1' | 'TRAY2' | 'MPTRAY' | 'MANUALFEED';
  okiMediaWeight?: 'LIGHT' | 'MEDIUM' | 'HEAVY' | 'ULTRAHEAVY';
  okiMediaType?: 'PLAIN' | 'LABELS' | 'BOND' | 'RECYCLED' | 'CARDSTOCK' | 'ROUGH';
  okiPaperSize?: 'FLEXI' | 'A4' | 'A5' | 'LEGAL' | 'OBLONG' | 'OVERSIZE' | '4X4_SQUARE' | 'JDM_IMPORT' | 'MOTO_9X7' | 'MOTO_MID' | 'MOTO_COMPACT' | 'MOTO_MINI' | 'CUSTOM';
  okiCustomPaperWidth?: number; // in mm (converted to decipoints when sending to printer)
  okiCustomPaperLength?: number; // in mm (converted to decipoints when sending to printer)
  okiResolution?: 600 | 1200;
  okiEconomode?: boolean; // false = OFF (dark, rich blacks), true = ON (toner save, lighter)
  okiDensity?: 1 | 2 | 3 | 4 | 5; // Darkness level (1-5, 5 being darkest)
  okiOrientation?: 'PORTRAIT' | 'LANDSCAPE';
  okiOffsetX?: number; // Printer command X offset in mm
  okiOffsetY?: number; // Printer command Y offset in mm
  okiCustomPjlHeader?: string; // Custom PJL header (optional) - use {WIDTH} and {HEIGHT} as placeholders
  // Citizen Thermal Printer Settings
  citizenSpeed?: number; // 0 = default, 1-6 = IPS
  citizenDarkness?: number; // -5 to +5, 0 = default
  citizenSensorType?: 'gap' | 'blackmark' | 'continuous';
  citizenAutoCut?: boolean; // Auto-cut after print (default true)
  citizenPrintMethod?: 'direct' | 'transfer'; // direct = Direct Thermal (no ribbon), transfer = Thermal Transfer (ribbon)
  // Continuous media settings (only used when citizenSensorType is 'continuous')
  citizenRollWidth?: number; // Width of continuous roll in mm
  citizenFeedBefore?: number; // Feed before print in mm (0-50)
  citizenFeedAfter?: number; // Feed after print in mm (0-50)
}

// OKI Paper Size Presets (dimensions in mm)
export const OKI_PAPER_SIZES: Record<string, { width: number; length: number; label: string }> = {
  'FLEXI': { width: 0, length: 0, label: 'Flexi (matches artwork)' },
  'A4': { width: 210, length: 297, label: 'A4 (210x297mm)' },
  'A5': { width: 148, length: 210, label: 'A5 (148x210mm)' },
  'LEGAL': { width: 216, length: 356, label: 'Legal (216x356mm)' },
  'OBLONG': { width: 111, length: 520, label: 'Oblong (111x520mm)' },
  'OVERSIZE': { width: 152, length: 533, label: 'Oversize (152x533mm)' },
  '4X4_SQUARE': { width: 203, length: 280, label: '4x4 Square (203x280mm)' },
  'JDM_IMPORT': { width: 165, length: 330, label: 'JDM Import (165x330mm)' },
  'MOTO_9X7': { width: 177, length: 228, label: 'Moto 9x7 (177x228mm)' },
  'MOTO_MID': { width: 158, length: 226, label: 'Moto Mid 8.5x6.5" (158x226mm)' },
  'MOTO_COMPACT': { width: 152, length: 203, label: 'Moto Compact 8x6" (152x203mm)' },
  'MOTO_MINI': { width: 127, length: 177, label: 'Moto Mini 7x5" (127x177mm)' },
  'CUSTOM': { width: 210, length: 297, label: 'Custom' },
};

// Convert mm to decipoints (1 inch = 720 decipoints, 1 inch = 25.4mm)
export const mmToDecipoints = (mm: number): number => Math.round(mm * 720 / 25.4);

export interface PrintSettings {
  dualMode: boolean;
  quickPrintEnabled: boolean;
  singleProfileId: string;
  dualWhiteProfileId: string;
  dualYellowProfileId: string;
}

export interface SavedTemplate {
  id: string;
  name: string;
  config: PlateConfig;
}

export type BulkColor = 'F' | 'R' | 'P'; 

export interface BulkItem {
  id: string;
  registration: string;
  type: BulkColor;
  border: boolean;
  flagLayout: boolean;
  selected: boolean; 
  status: {
    printedWhite: boolean;
    printedYellow: boolean;
  }
}

export interface RNPSRecord {
  registration: string;
  date: string;
  customerName: string;
  customerAddress: string;
  idType: string;
  idReference: string;
  entitlementType: string;
  entitlementReference: string;
  idImage: string | null;
  entitlementImage: string | null;
}

export const InitialDimensions = { ...PlateDimensions };
